import { useState, useEffect } from "react";
import { useApp } from "../../contexts/AppContext";

interface Slide {
  icon: string;
  title: string;
  desc: string;
  color: string;
  badge?: string;
}

const consumerSlides: Slide[] = [
  {
    icon: "ri-map-pin-user-line",
    title: "利用目的を設定する",
    desc: "プロフィール設定から「学習の利用目的」を選ぶと、あなたに合った講座や事業者が優先表示されます。登録直後に設定でき、後からマイページでいつでも変更できます。",
    color: "from-teal/20 to-teal/5",
    badge: "まずはここから",
  },
  {
    icon: "ri-search-2-line",
    title: "事業者・講座を探す",
    desc: "ホームのカテゴリナビや検索から学びたい分野を選択。各事業者ページには無料サンプル動画があり、購入前に技術の深さを確認できます。",
    color: "from-deep-green/20 to-deep-green/5",
  },
  {
    icon: "ri-play-circle-line",
    title: "動画講座・教材キットを購入",
    desc: "気に入った講座や教材キットを購入。何度でも繰り返し視聴・参照できます。学習の進捗はマイページの「学習中」タブで一目で確認できます。",
    color: "from-gold/20 to-gold/5",
  },
  {
    icon: "ri-message-3-line",
    title: "職人へ直接メッセージ",
    desc: "購入済みの講座・教材がある事業者には、メッセージで直接質問できます。疑問点はすぐ解消。プロの視点からの回答が学習をより深めます。",
    color: "from-teal/20 to-teal/5",
  },
  {
    icon: "ri-store-2-line",
    title: "事業者モードに切り替え可能",
    desc: "受講者モードと事業者モードは、ナビバーのモード切替ボタンでいつでも切り替えられます。一つのアカウントで学ぶことも教えることも、両方できます。",
    color: "from-amber-400/20 to-amber-400/5",
    badge: "便利な機能",
  },
];

const providerSlides: Slide[] = [
  {
    icon: "ri-store-2-line",
    title: "事業者プロフィールを充実させる",
    desc: "あなたの技術・経歴・専門分野をプロフィールに記入しましょう。サービス名・自己紹介・カバー画像を設定することで、受講者からの信頼につながります。",
    color: "from-amber-400/20 to-amber-400/5",
    badge: "まずはここから",
  },
  {
    icon: "ri-video-line",
    title: "動画講座を出品する",
    desc: "無料サンプルと有料コンテンツを組み合わせた講座を作成。動画の長さに応じた推奨価格を自動表示。カテゴリがない場合はフォームから即時追加できます。",
    color: "from-gold/20 to-gold/5",
  },
  {
    icon: "ri-box-3-line",
    title: "教材キットを販売する",
    desc: "実践に必要な道具・材料セットを販売できます。仕入れ価格を入力すれば推奨販売価格を自動計算。在庫管理・発送情報も設定可能です。",
    color: "from-amber-400/20 to-amber-400/5",
  },
  {
    icon: "ri-bar-chart-2-line",
    title: "売上・分析でビジネスを育てる",
    desc: "「売上・分析」タブで月別売上推移・商品別ランキング・購入者の利用目的分布を確認できます。どの受講者層に人気かを把握し、講座づくりに活かせます。",
    color: "from-gold/20 to-gold/5",
  },
  {
    icon: "ri-exchange-line",
    title: "モードはいつでも切り替えOK",
    desc: "事業者モードを受講者モードに切り替えても、出品した商品はサイト上にそのまま残り続けます。モードはあくまで表示の切り替えであり、データは消えません。",
    color: "from-teal/20 to-teal/5",
    badge: "重要",
  },
];

export default function TutorialModal() {
  const { isLoggedIn, mode, showTutorial, closeTutorial } = useApp();
  const [step, setStep] = useState(0);
  const [localVisible, setLocalVisible] = useState(false);

  useEffect(() => {
    if (isLoggedIn && !localVisible) {
      const done = localStorage.getItem("tsugite_tutorial_done");
      if (!done) {
        setLocalVisible(true);
        setStep(0);
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (showTutorial) {
      setLocalVisible(true);
      setStep(0);
    }
  }, [showTutorial]);

  const handleClose = () => {
    setLocalVisible(false);
    closeTutorial();
  };

  const slides = mode === "provider" ? providerSlides : consumerSlides;
  const isLast = step === slides.length - 1;
  const s = slides[step];

  if (!localVisible) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full max-w-lg bg-white rounded-3xl overflow-hidden shadow-2xl">
        {/* Progress bar */}
        <div className="h-1 bg-stone-100">
          <div
            className="h-full bg-gradient-to-r from-teal to-deep-green transition-all duration-500"
            style={{ width: `${((step + 1) / slides.length) * 100}%` }}
          />
        </div>

        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 text-charcoal/50 hover:bg-stone-200 cursor-pointer transition-colors z-10"
        >
          <i className="ri-close-line text-base" />
        </button>

        {/* Content */}
        <div className={`bg-gradient-to-b ${s.color} px-8 pt-14 pb-8 text-center min-h-[300px] flex flex-col items-center justify-center`}>
          {s.badge && (
            <span className="bg-gold text-white text-xs px-3 py-0.5 rounded-full font-sans mb-4">{s.badge}</span>
          )}
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white mb-5 shadow-sm">
            <i className={`${s.icon} text-4xl text-deep-green`} />
          </div>
          <h2 className="font-serif text-deep-green text-2xl mb-3">{s.title}</h2>
          <p className="text-charcoal/70 font-sans text-sm leading-relaxed max-w-sm">{s.desc}</p>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 flex items-center justify-between gap-4 border-t border-stone-100 bg-white">
          {/* Step info + dots */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-charcoal/40 font-sans whitespace-nowrap">{step + 1} / {slides.length}</span>
            <div className="flex gap-1.5">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${i === step ? "w-6 h-2 bg-deep-green" : "w-2 h-2 bg-stone-200 hover:bg-stone-300"}`}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 hover:border-stone-300 cursor-pointer whitespace-nowrap"
              >
                ← 前へ
              </button>
            )}
            {isLast ? (
              <button
                onClick={handleClose}
                className="px-6 py-2 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap"
              >
                さっそく始める →
              </button>
            ) : (
              <button
                onClick={() => setStep(step + 1)}
                className="px-6 py-2 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap"
              >
                次へ →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
